import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
    Users, Search, Filter, Zap, HardHat, Building2, Phone, Mail, 
    MapPin, ChevronLeft, ChevronRight, Loader2, Eye, UserCheck,
    Download, Send
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { clientsAPI } from '@/lib/api';
import { toast } from 'sonner';

export default function Clients() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [clients, setClients] = useState([]);
    const [stats, setStats] = useState(null);
    const [search, setSearch] = useState('');
    const [pipelineFilter, setPipelineFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;

    const loadClients = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: 20,
                search: search || undefined,
                pipeline_filter: pipelineFilter === 'all' ? undefined : pipelineFilter
            };
            
            const res = await clientsAPI.getAll(params);
            setClients(res.data.clients);
            setTotalPages(res.data.pages);
            setTotal(res.data.total);
        } catch (err) {
            toast.error('Erreur lors du chargement des clients');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [page, search, pipelineFilter]);

    const loadStats = useCallback(async () => {
        try {
            const res = await clientsAPI.getStats();
            setStats(res.data);
        } catch (err) {
            console.error('Erreur chargement stats:', err);
        }
    }, []);

    useEffect(() => {
        loadClients();
    }, [loadClients]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadClients();
    };

    const viewClient = (clientId) => {
        navigate(`/prospects/${clientId}`);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getClientTypeBadges = (client) => {
        const badges = [];
        if (client.client_type?.includes('easy_power')) {
            badges.push(
                <Badge key="ep" className="bg-amber-100 text-amber-800 text-xs">
                    <Zap className="w-3 h-3 mr-1" />
                    Courtage
                </Badge>
            );
        }
        if (client.client_type?.includes('chantier')) {
            badges.push(
                <Badge key="ch" className="bg-blue-100 text-blue-800 text-xs">
                    <HardHat className="w-3 h-3 mr-1" />
                    Travaux d’optimisation
                </Badge>
            );
        }
        return badges;
    };

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
                            <UserCheck className="w-6 h-6 text-emerald-600" />
                            Clients
                        </h1>
                        <p className="text-slate-600 mt-1 text-sm">
                            Base de données de vos clients
                        </p>
                    </div>
                    {isAdmin && (
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" className="gap-1.5">
                                <Download className="w-4 h-4" />
                                <span className="hidden sm:inline">Exporter</span>
                            </Button>
                            <Button variant="outline" size="sm" className="gap-1.5">
                                <Send className="w-4 h-4" />
                                <span className="hidden sm:inline">Mailing</span>
                            </Button>
                        </div>
                    )}
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-emerald-100 rounded-lg">
                                        <Users className="w-5 h-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-slate-900">{stats.total_clients}</p>
                                        <p className="text-sm text-slate-500">Total Clients</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-amber-100 rounded-lg">
                                        <Zap className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-slate-900">{stats.clients_easy_power}</p>
                                        <p className="text-sm text-slate-500">Courtage</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 rounded-lg">
                                        <HardHat className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-slate-900">{stats.clients_chantier}</p>
                                        <p className="text-sm text-slate-500">Travaux d’optimisation</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-purple-100 rounded-lg">
                                        <UserCheck className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-slate-900">{stats.clients_both_pipelines}</p>
                                        <p className="text-sm text-slate-500">Les deux</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Filters */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row gap-4">
                            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        placeholder="Rechercher par nom, email, ville, SIRET..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        className="pl-10"
                                        data-testid="clients-search"
                                    />
                                </div>
                                <Button type="submit" variant="secondary">
                                    Rechercher
                                </Button>
                            </form>
                            <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-slate-400" />
                                <Select value={pipelineFilter} onValueChange={(val) => { setPipelineFilter(val); setPage(1); }}>
                                    <SelectTrigger className="w-48" data-testid="pipeline-filter">
                                        <SelectValue placeholder="Filtrer par type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les clients</SelectItem>
                                        <SelectItem value="easy_power">Courtage uniquement</SelectItem>
                                        <SelectItem value="chantier">Travaux d’optimisation uniquement</SelectItem>
                                        <SelectItem value="both">Les deux pipelines</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Clients Table */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center justify-between">
                            <span>Liste des Clients ({total})</span>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                            </div>
                        ) : clients.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                <UserCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                                <p>Aucun client trouvé</p>
                                <p className="text-sm mt-1">Les prospects finalisés apparaîtront ici</p>
                            </div>
                        ) : (
                            <>
                                <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Entreprise</TableHead>
                                            <TableHead>Contact</TableHead>
                                            <TableHead>Ville</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Responsable</TableHead>
                                            <TableHead>Date création</TableHead>
                                            <TableHead className="w-12"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {clients.map((client) => (
                                            <TableRow 
                                                key={client.id} 
                                                className="cursor-pointer hover:bg-slate-50"
                                                onClick={() => viewClient(client.id)}
                                                data-testid={`client-row-${client.id}`}
                                            >
                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <Building2 className="w-4 h-4 text-slate-400" />
                                                        <div>
                                                            <p className="font-medium">{client.company_name || '-'}</p>
                                                            {client.siret && (
                                                                <p className="text-xs text-slate-400">SIRET: {client.siret}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="space-y-1">
                                                        <p className="font-medium">{client.contact_name || '-'}</p>
                                                        {client.email && (
                                                            <div className="flex items-center gap-1 text-xs text-slate-500">
                                                                <Mail className="w-3 h-3" />
                                                                {client.email}
                                                            </div>
                                                        )}
                                                        {client.phone && (
                                                            <div className="flex items-center gap-1 text-xs text-slate-500">
                                                                <Phone className="w-3 h-3" />
                                                                {client.phone}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1 text-sm">
                                                        <MapPin className="w-3 h-3 text-slate-400" />
                                                        {client.city || '-'}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex flex-wrap gap-1">
                                                        {getClientTypeBadges(client)}
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="text-sm">
                                                        {client.dossier_vp_name && (
                                                            <p className="text-purple-600">{client.dossier_vp_name}</p>
                                                        )}
                                                        {client.dossier_apporteur_name && (
                                                            <p className="text-green-600 text-xs">{client.dossier_apporteur_name}</p>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-slate-500">
                                                    {formatDate(client.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={(e) => { e.stopPropagation(); viewClient(client.id); }}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                        <p className="text-sm text-slate-500">
                                            Page {page} sur {totalPages} ({total} clients)
                                        </p>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                            >
                                                <ChevronLeft className="w-4 h-4 mr-1" />
                                                Précédent
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                disabled={page === totalPages}
                                            >
                                                Suivant
                                                <ChevronRight className="w-4 h-4 ml-1" />
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
